import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { shootCoinsFrom, playCoins, playRockRiff } from "@/lib/confetti";
import CoinStreakBanner from "@/components/CoinStreakBanner";

function animateNumber(set, from, to, ms) {
  const start = performance.now();
  const tick = (now) => {
    const p = Math.min(1, (now - start) / ms);
    set(Math.round(from + (to - from) * (1 - Math.pow(1 - p, 3))));
    if (p < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

// Exaggerated comic punk rocker: giant spiked hair, big head, oversized boots.
function Rocker({ gender, hunched, facingFront }) {
  const girl = gender === "girl";
  const hair = girl ? "#FF2D95" : "#39FF14";
  const jacket = girl ? "#2D2D2D" : "#1A1A1A";
  const pants = girl ? "#7B2D8B" : "#B3282D";
  return (
    <svg width="120" height="150" viewBox="0 0 120 150" style={{ overflow: "visible" }}>
      <g transform={hunched ? "rotate(22 60 140)" : "rotate(0 60 140)"} style={{ transition: "transform .5s" }}>
        {/* oversized boots */}
        <ellipse cx="42" cy="144" rx="20" ry="8" fill="#111" />
        <ellipse cx="80" cy="144" rx="20" ry="8" fill="#111" />
        {/* tartan pants */}
        <rect x="42" y="104" width="14" height="38" fill={pants} rx="5" />
        <rect x="66" y="104" width="14" height="38" fill={pants} rx="5" />
        <line x1="44" y1="112" x2="56" y2="112" stroke="#000" strokeWidth="2" opacity="0.4" />
        <line x1="68" y1="120" x2="80" y2="120" stroke="#000" strokeWidth="2" opacity="0.4" />
        {/* studded leather jacket */}
        <rect x="36" y="66" width="50" height="46" rx="12" fill={jacket} />
        {[0, 1, 2, 3].map((i) => <circle key={i} cx={44 + i * 11} cy="74" r="2.4" fill="#C0C0C0" />)}
        {girl && <rect x="38" y="104" width="46" height="10" rx="4" fill={pants} />}
        {/* arms */}
        <rect x="28" y="70" width="12" height="34" rx="6" fill={jacket} />
        <rect x="82" y="70" width="12" height="34" rx="6" fill={jacket} />
        {/* oversized head */}
        <circle cx="61" cy="42" r="26" fill="#FFD9B3" />
        {facingFront ? (
          <g>
            <circle cx="52" cy="40" r="3.4" fill="#111" />
            <circle cx="70" cy="40" r="3.4" fill="#111" />
            <path d="M50 53 q11 8 22 0" stroke="#111" strokeWidth="3" fill="none" strokeLinecap="round" />
            {girl && <path d="M46 34 l6 -3 M76 34 l-6 -3" stroke="#111" strokeWidth="2.4" strokeLinecap="round" />}
          </g>
        ) : (
          <circle cx="74" cy="41" r="3.4" fill="#111" />
        )}
        {/* giant hair spikes */}
        <g fill={hair} stroke="#111" strokeWidth="1.6">
          <path d="M38 30 L20 -2 L46 22 Z" />
          <path d="M48 22 L44 -16 L60 20 Z" />
          <path d="M62 20 L72 -18 L74 22 Z" />
          <path d="M76 24 L98 -6 L84 30 Z" />
          <path d="M34 40 L8 26 L38 46 Z" />
        </g>
      </g>
    </svg>
  );
}

function Guitar() {
  return (
    <svg width="104" height="46" viewBox="0 0 104 46" style={{ overflow: "visible" }}>
      <rect x="4" y="19" width="62" height="8" rx="3" fill="#8D5524" />
      {[0, 1, 2].map((i) => <rect key={i} x={12 + i * 14} y="18" width="2.5" height="10" fill="#E8C88F" />)}
      <rect x="0" y="15" width="10" height="16" rx="3" fill="#111" />
      <path d="M66 5 q22 -8 32 8 q8 12 -4 22 q-16 12 -30 -2 q-8 -9 2 -28z" fill="#E53935" stroke="#7A1815" strokeWidth="2.5" />
      <circle cx="84" cy="23" r="6" fill="#111" />
      {[0, 1, 2].map((i) => <line key={i} x1="8" y1={21 + i * 2} x2="88" y2={21 + i * 2} stroke="#DDD" strokeWidth="0.9" />)}
    </svg>
  );
}

// Punk rocker celebration: shuffles on hunched over dragging a guitar, turns to
// face the crowd, shreds an angry riff while coins erupt off the strings, then
// drags the guitar back off stage while the last coins rain down.
export default function PunkRockerCelebration({
  coinsAwarded, totalCoins, scorePercent,
  canAdvance, onRetry, onContinue, soundOn = true, hasPrev = false, onPrev, onDone, gender = "boy",
}) {
  const prevTotal = Math.max(0, (totalCoins || 0) - (coinsAwarded || 0));
  const [phase, setPhase] = useState("walkIn"); // walkIn -> turn -> shred -> tail -> walkOff -> done
  const [count, setCount] = useState(0);
  const [total, setTotal] = useState(prevTotal);
  const timers = useRef([]);
  const rootRef = useRef(null);
  const sceneRef = useRef(null);
  const doneRef = useRef(null);
  const shredding = phase === "shred";
  const showScore = ["shred", "tail", "walkOff", "done"].includes(phase);

  const burst = () => {
    const r = sceneRef.current?.getBoundingClientRect();
    if (!r) return;
    shootCoinsFrom((r.left + r.width * 0.52) / window.innerWidth, (r.top + r.height * 0.62) / window.innerHeight);
  };

  useEffect(() => {
    const add = (fn, ms) => timers.current.push(setTimeout(fn, ms));
    add(() => setPhase("turn"), 2500);
    add(() => {
      setPhase("shred");
      if (soundOn) playRockRiff(3);
      animateNumber(setCount, 0, coinsAwarded, 1400);
      animateNumber(setTotal, prevTotal, totalCoins, 1600);
      for (let i = 0; i < 8; i++) add(() => { burst(); if (soundOn && i % 2 === 0) playCoins(5); }, 300 + i * 340);
    }, 3300);
    add(() => { setPhase("tail"); if (soundOn) playCoins(9); }, 6500);
    add(() => { setPhase("walkOff"); if (soundOn) playCoins(6); }, 7400);
    add(() => {
      setPhase("done");
      onDone && onDone();
    }, 8900);
    return () => timers.current.forEach(clearTimeout);
    // eslint-disable-next-line
  }, []);

  const walking = phase === "walkIn" || phase === "walkOff" || phase === "done";
  const leaving = phase === "walkOff" || phase === "done";
  const hunched = phase === "walkIn" || leaving;
  const charAnim =
    phase === "walkIn" ? { x: 0, transition: { duration: 2.4, ease: "linear" } }
    : leaving ? { x: -560, transition: { duration: 2.8, ease: "linear" } }
    : { x: 0 };
  // Guitar: dragged flat on the ground behind while walking, swung up to playing position on stage.
  const guitarAnim = hunched
    ? { rotate: 24, x: -46, y: 44, transition: { duration: 0.45 } }
    : { rotate: -14, x: 0, y: 0, transition: { duration: 0.6, ease: "easeOut" } };

  return (
    <div ref={rootRef} className="text-center py-2 relative scroll-mt-24" data-testid="punkrocker-celebration">
      {/* the stage */}
      <div ref={sceneRef} className="relative mx-auto w-full max-w-md h-60 overflow-hidden rounded-2xl"
        style={{ background: "linear-gradient(180deg,#1A1030 0%,#33194F 58%,#241436 58%,#150B22 100%)" }}
        data-testid="punkrocker-scene">
        {/* stage spotlights */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 45% at 50% 30%, rgba(255,230,120,0.28), transparent 70%)" }} />
        {shredding && (
          <motion.div className="absolute inset-0 pointer-events-none"
            animate={{ opacity: [0.1, 0.45, 0.15, 0.5, 0.1] }} transition={{ duration: 0.6, repeat: 4 }}
            style={{ background: "radial-gradient(ellipse 70% 55% at 50% 40%, rgba(255,45,149,0.25), transparent 70%)" }} />
        )}
        {/* stage floor edge */}
        <div className="absolute left-0 right-0" style={{ bottom: 0, height: 12, background: "#0B0614" }} />

        {/* the rocker (parked centre stage; walks in/out via x) */}
        <motion.div className="absolute" style={{ left: "50%", marginLeft: -60, bottom: 6, width: 120, height: 150 }}
          initial={{ x: -560 }} animate={charAnim} data-testid="punkrocker-character">
          <motion.div
            animate={walking ? { y: [0, -4, 0], rotate: [0, 2, 0, -2, 0] } : shredding ? { y: [0, -2, 0] } : { y: 0 }}
            transition={walking ? { duration: 0.55, repeat: Infinity, ease: "easeInOut" } : shredding ? { duration: 0.22, repeat: Infinity } : { duration: 0.2 }}
            style={{ position: "relative", width: 120, height: 150 }}
          >
            <div style={{ position: "absolute", bottom: 0, left: 0, transform: leaving ? "scaleX(-1)" : "none" }}>
              <Rocker gender={gender} hunched={hunched} facingFront={!hunched} />
            </div>
            {/* guitar — dragged behind on the ground, then swung up to play */}
            <motion.div style={{ position: "absolute", left: 10, bottom: 40, transformOrigin: "20px 24px", transform: leaving ? "scaleX(-1)" : "none" }}
              animate={guitarAnim} data-testid="punkrocker-guitar">
              <motion.div animate={shredding ? { rotate: [0, -3, 2, -2, 0] } : {}} transition={{ duration: 0.18, repeat: shredding ? Infinity : 0 }}>
                <Guitar />
              </motion.div>
            </motion.div>
            {/* strumming hand blur */}
            {shredding && (
              <motion.div className="absolute rounded-full" style={{ left: 86, bottom: 52, width: 16, height: 16, background: "#FFD9B3", opacity: 0.9 }}
                animate={{ y: [0, 10, 0] }} transition={{ duration: 0.12, repeat: Infinity }} />
            )}
            {/* angry music notes while shredding */}
            {shredding && (
              <div className="absolute" style={{ top: -10, left: 20 }}>
                {["♬", "♪", "♩", "♫"].map((n, i) => (
                  <motion.span key={i} className="absolute font-bold" style={{ fontSize: 20, left: i * 18, color: i % 2 ? "#FF2D95" : "#39FF14" }}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: [0, 1, 0], y: -30, rotate: i % 2 ? 18 : -18 }}
                    transition={{ duration: 0.9, delay: i * 0.22, repeat: Infinity, repeatDelay: 0.2 }}>
                    {n}
                  </motion.span>
                ))}
              </div>
            )}
          </motion.div>
        </motion.div>
      </div>

      {/* Coin bubbles live BELOW the scene and are always mounted (opacity fade only),
          stacked vertically — the scene above never moves when they appear or count up. */}
      <div className={`flex flex-col items-center gap-1.5 mt-2 transition-opacity duration-500 ${showScore ? "opacity-100" : "opacity-0"}`}>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-2xl font-extrabold font-display"
          style={{ background: "linear-gradient(135deg,#FFD600,#FF9E00)", color: "#3d2c00", boxShadow: "0 8px 24px rgba(255,158,0,0.45)" }}
          data-testid="quiz-xp-awarded">
          <span className="text-2xl">🪙</span>
          <span className="text-xl sm:text-2xl leading-none">+{count.toLocaleString()} coins today</span>
        </div>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-2xl font-extrabold font-display"
          style={{ background: "linear-gradient(135deg,#FFD600,#FF9E00)", color: "#3d2c00", boxShadow: "0 8px 24px rgba(255,158,0,0.45)" }}
          data-testid="coin-grand-total">
          <span className="text-2xl">🪙</span>
          <span className="text-3xl leading-none text-outline-lime">{total.toLocaleString()}</span>
          <span className="text-xl leading-none">total</span>
        </div>
        {scorePercent != null && (
          <div className="text-3xl font-extrabold font-display text-[#8fb300]" data-testid="quiz-score">{scorePercent}%</div>
        )}
      </div>

      {showScore && <CoinStreakBanner celebrationsEnabled={soundOn} compact className="mt-2 max-w-md mx-auto justify-center text-center" />}

      <AnimatePresence>
        {phase === "done" && (
          <motion.div ref={doneRef} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }} data-testid="coin-done" className="scroll-mb-24">
            <p className="text-black font-semibold max-w-md mx-auto mt-2" data-testid="quiz-encouragement">
              You rocked that one — encore on the next lesson!
            </p>
            <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
              {hasPrev && (
                <button data-testid="quiz-prev-button" className="btn-secondary text-sm px-4 py-2 whitespace-nowrap" onClick={() => onPrev && onPrev()}>
                  ← Prev
                </button>
              )}
              {onRetry && <button data-testid="quiz-retry-button" className="btn-secondary text-sm px-4 py-2 whitespace-nowrap" onClick={onRetry}>Redo</button>}
              {canAdvance && onContinue && (
                <button data-testid="quiz-continue-button" className="btn-primary text-sm px-4 py-2 whitespace-nowrap" onClick={onContinue}>
                  Next →
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
